import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { Chapter } from "../../types";
import { PanelHeader } from "./PanelHeader";
import { ExtractToolbar } from "./ExtractToolbar";
import { EmptyState } from "../ui/empty-state";
import { Field } from "../ui/field";
import { SectionCard } from "../ui/section-card";
import { cn } from "../../lib/utils";
import { IconOpenIn, IconRemove } from "./icons";
import {
  ARC_LEVER_CATEGORIES,
  ARC_LEVER_HINTS,
  ARC_LEVER_LABELS,
  appearanceForChapter,
  charactersInChapter,
  createBlankLevers,
  leverDelta,
  leverDeltaSymbol,
  previousAppearance,
  type BookCharacter,
  type CharacterArcLever,
  type CharacterRole,
} from "../../../shared/characterRoster";

function scoreColor(score: number): string {
  if (score >= 4) return "var(--score-good)";
  if (score >= 3) return "var(--score-fair)";
  return "var(--score-poor)";
}

function LeverRow({
  lever,
  delta,
  onChange,
}: {
  lever: CharacterArcLever;
  delta: ReturnType<typeof leverDelta>;
  onChange: (next: CharacterArcLever) => void;
}) {
  return (
    <div className="flex flex-col gap-1 rounded bg-muted/20 px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="min-w-26 text-xs font-semibold">{ARC_LEVER_LABELS[lever.category]}</span>
        <span
          className={cn(
            "min-w-4 text-center text-sm font-bold",
            delta === "up" && "text-green-500",
            delta === "down" && "text-red-500",
            (delta === "flat" || delta === "new") && "text-muted-foreground"
          )}
          title={
            delta === "up"
              ? "Up from previous chapter"
              : delta === "down"
                ? "Down from previous chapter"
                : delta === "new"
                  ? "First scored appearance"
                  : "Unchanged"
          }
        >
          {leverDeltaSymbol(delta)}
        </span>
        <Select
          value={String(lever.score)}
          onValueChange={(value) => onChange({ ...lever, score: Number(value) })}
        >
          <SelectTrigger
            className="min-w-16"
            size="sm"
            aria-label={`${ARC_LEVER_LABELS[lever.category]} score`}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[1, 2, 3, 4, 5].map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span
          className="size-2.25 rounded-full"
          style={{ background: scoreColor(lever.score) }}
          aria-hidden
        />
      </div>
      <p className="text-xs text-muted-foreground">{ARC_LEVER_HINTS[lever.category]}</p>
      <Textarea
        aria-label={`${ARC_LEVER_LABELS[lever.category]} evidence`}
        className="min-h-0! text-[0.8rem]!"
        placeholder="Key evidence from text…"
        value={lever.evidence}
        rows={2}
        onChange={(e) => onChange({ ...lever, evidence: e.target.value })}
      />
    </div>
  );
}

function ChapterCharacterCard({
  character,
  chapter,
  chapters,
  onUpdateCharacter,
  onOpenInGuide,
  onRequestRemove,
}: {
  character: BookCharacter;
  chapter: Chapter;
  chapters: Array<{ id: string; order: number }>;
  onUpdateCharacter: (id: string, patch: Partial<BookCharacter>) => void;
  onOpenInGuide: (id: string) => void;
  onRequestRemove: (character: BookCharacter) => void;
}) {
  const appearance = appearanceForChapter(character, chapter.id);
  const prev = previousAppearance(character, chapter.id, chapters);
  const levers =
    character.role === "major"
      ? appearance?.levers?.length
        ? appearance.levers
        : createBlankLevers()
      : [];

  function updateLever(category: CharacterArcLever["category"], next: CharacterArcLever): void {
    const nextLevers = ARC_LEVER_CATEGORIES.map((cat) =>
      cat === category ? next : levers.find((l) => l.category === cat) ?? { category: cat, score: 3, evidence: "" }
    );
    const other = character.appearances.filter((a) => a.chapterId !== chapter.id);
    onUpdateCharacter(character.id, {
      appearances: [
        ...other,
        {
          chapterId: chapter.id,
          note: appearance?.note,
          levers: nextLevers,
        },
      ],
    });
  }

  function updateNote(note: string): void {
    const other = character.appearances.filter((a) => a.chapterId !== chapter.id);
    onUpdateCharacter(character.id, {
      appearances: [
        ...other,
        {
          chapterId: chapter.id,
          note,
          ...(character.role === "major" ? { levers } : {}),
        },
      ],
    });
  }

  function setRole(role: CharacterRole): void {
    const other = character.appearances.filter((a) => a.chapterId !== chapter.id);
    const thisAppearance = {
      chapterId: chapter.id,
      note: appearance?.note,
      ...(role === "major" ? { levers: appearance?.levers?.length ? appearance.levers : createBlankLevers() } : {}),
    };
    onUpdateCharacter(character.id, {
      role,
      appearances: [...other, thisAppearance],
    });
  }

  return (
    <SectionCard
      bodyClassName="flex flex-col gap-2.5"
      title={
        <span className="inline-flex items-center gap-2">
          {character.color ? (
            <span
              className="inline-block size-3.5 shrink-0 rounded-[3px] border border-foreground/20 align-middle"
              style={{ backgroundColor: character.color.highlight }}
              aria-hidden
            />
          ) : null}
          {character.name}
        </span>
      }
      actions={
        <>
          <Select value={character.role} onValueChange={(value) => setRole(value as CharacterRole)}>
            <SelectTrigger className="min-w-26" size="sm" aria-label={`${character.name} role`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="major">Major</SelectItem>
              <SelectItem value="minor">Minor</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="icon-sm"
            variant="ghost"
            type="button"
            aria-label="Open in Characters"
            title="Open in Characters"
            onClick={() => onOpenInGuide(character.id)}
          >
            <IconOpenIn />
          </Button>
          <Button
            size="icon-sm"
            variant="destructive"
            type="button"
            aria-label="Remove character"
            title="Remove character"
            onClick={() => onRequestRemove(character)}
          >
            <IconRemove />
          </Button>
        </>
      }
    >
      {character.aliases.length > 0 ? (
        <p className="text-xs text-muted-foreground">Also: {character.aliases.join(", ")}</p>
      ) : null}

      <Field label="Who they are (book)">
        <Textarea
          aria-label={`${character.name} description`}
          className="min-h-0! text-[0.8rem]!"
          placeholder="Lasting identity: role, relationships, traits…"
          value={character.description}
          rows={2}
          onChange={(e) => onUpdateCharacter(character.id, { description: e.target.value })}
        />
      </Field>

      <Field label="What changed this chapter">
        <Textarea
          aria-label={`${character.name} chapter note`}
          className="min-h-0! text-[0.8rem]!"
          placeholder="Costume, mood, actions in this chapter only…"
          value={appearance?.note ?? ""}
          rows={2}
          onChange={(e) => updateNote(e.target.value)}
        />
      </Field>

      {character.role === "major" ? (
        <div className="mt-1 flex flex-col gap-2 border-t border-border pt-2">
          <div className="text-xs font-medium">Arc levers</div>
          {levers.map((lever) => {
            const prevLever = prev?.levers?.find((l) => l.category === lever.category);
            return (
              <LeverRow
                key={lever.category}
                lever={lever}
                delta={leverDelta(lever, prevLever)}
                onChange={(next) => updateLever(lever.category, next)}
              />
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Minor character — no arc levers.</p>
      )}
    </SectionCard>
  );
}

export function ChapterCharactersPanel({
  activeChapter,
  chapters,
  roster,
  busy,
  extractModel,
  onExtractModelChange,
  openAiModels,
  anthropicModels,
  onExtract,
  onUpdateCharacter,
  onAddCharacter,
  onOpenInGuide,
  onRequestRemove,
}: {
  activeChapter: Chapter;
  chapters: Array<{ id: string; order: number }>;
  roster: BookCharacter[];
  busy: boolean;
  extractModel: string;
  onExtractModelChange: (model: string, provider: "openai" | "anthropic") => void;
  openAiModels: ReadonlyArray<{ value: string; label: string }>;
  anthropicModels: ReadonlyArray<{ value: string; label: string }>;
  onExtract: () => void;
  onUpdateCharacter: (id: string, patch: Partial<BookCharacter>) => void;
  onAddCharacter: (role: CharacterRole) => void;
  onOpenInGuide: (id: string) => void;
  onRequestRemove: (character: BookCharacter) => void;
}) {
  const inChapter = charactersInChapter(roster, activeChapter.id);

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <PanelHeader
        title="Characters in this chapter"
        description="Majors include arc levers (Baseline → Resolution). Extract merges into the book roster."
        actions={
          <>
            <ExtractToolbar
              model={extractModel}
              onModelChange={onExtractModelChange}
              openAiModels={openAiModels}
              anthropicModels={anthropicModels}
              onExtract={onExtract}
              extractBusy={busy}
              extractLabel="Extract characters"
            />
            <Button size="sm" variant="secondary" type="button" onClick={() => onAddCharacter("major")} disabled={busy}>
              Add major
            </Button>
            <Button size="sm" variant="secondary" type="button" onClick={() => onAddCharacter("minor")} disabled={busy}>
              Add minor
            </Button>
          </>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto px-4 pt-3.5 pb-5">
        {inChapter.length === 0 ? (
          <EmptyState
            title="No characters linked to this chapter yet."
            description="Run Extract characters to pull majors and minors from the manuscript, or add one manually."
          />
        ) : (
          inChapter.map((character) => (
            <ChapterCharacterCard
              key={character.id}
              character={character}
              chapter={activeChapter}
              chapters={chapters}
              onUpdateCharacter={onUpdateCharacter}
              onOpenInGuide={onOpenInGuide}
              onRequestRemove={onRequestRemove}
            />
          ))
        )}
      </div>
    </div>
  );
}
